import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Image,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { useEffect, useMemo, useState } from "react";

import { Fetch, Post } from "../../fetch";

const { Option } = Select;
const { Text, Paragraph } = Typography;

const CATEGORIES = [
  { value: "non_gaming", label: "Non Gaming", color: "blue" },
  { value: "gaming", label: "Gaming", color: "purple" },
  { value: "survey", label: "Survey", color: "green" },
];

const formatCoins = (n) => {
  const num = Number(n) || 0;
  return Math.round(num).toLocaleString("en-IN");
};

/**
 * Browse LIVE production PubScale offers (via setu proxy), preview goals /
 * stepwise instructions / coins / categories like the app, then multi-select
 * with a per-row category and bulk-add to the curated list.
 */
const BrowseLiveOffers = ({
  visible,
  onClose,
  apiUrl,
  token,
  existingOfferIds,
  nextOrderForCategory,
  onAdded,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [offers, setOffers] = useState([]);
  const [search, setSearch] = useState("");
  // offer_id -> { checked, category }
  const [rowState, setRowState] = useState({});
  const [adding, setAdding] = useState(false);

  const load = (force = false) => {
    setLoading(true);
    setErrorMsg(null);
    Fetch(
      `${apiUrl}/v4/tasks-config/pubscale/live-offers${force ? "?force=true" : ""}`,
      token,
      (result) => {
        setLoading(false);
        if (result && result.success && Array.isArray(result.offers)) {
          setOffers(result.offers);
        } else {
          setErrorMsg(
            (result && result.message) ||
              "Could not load live offers. Check that PUBSCALE_PUB_KEY is set on setu."
          );
        }
      }
    );
  };

  useEffect(() => {
    if (visible) {
      setSearch("");
      setRowState({});
      load(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const existing = useMemo(
    () => new Set((existingOfferIds || []).map((x) => String(x))),
    [existingOfferIds]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return offers;
    return offers.filter(
      (o) =>
        String(o.offer_id).includes(q) ||
        (o.name || "").toLowerCase().includes(q)
    );
  }, [offers, search]);

  const rowFor = (offer) =>
    rowState[offer.offer_id] || {
      checked: false,
      category: offer.suggested_category || "non_gaming",
    };

  const setRow = (offerId, patch) =>
    setRowState((prev) => ({
      ...prev,
      [offerId]: { ...(prev[offerId] || {}), ...patch },
    }));

  const selectedOffers = filtered.filter(
    (o) => rowFor(o).checked && !existing.has(String(o.offer_id))
  );

  const handleAdd = async () => {
    if (selectedOffers.length === 0) return;
    setAdding(true);

    // Track a running per-category display_order so newly-added offers append.
    const orderCursor = {};
    const nextOrder = (category) => {
      if (orderCursor[category] == null) {
        orderCursor[category] = nextOrderForCategory(category);
      }
      const v = orderCursor[category];
      orderCursor[category] = v + 1;
      return v;
    };

    const postOffer = (payload) =>
      new Promise((resolve) => {
        Post(
          `${apiUrl}/v4/tasks-config/offers`,
          token,
          payload,
          (result) => resolve(Boolean(result && result.success)),
          () => resolve(false)
        );
      });

    const results = [];
    for (const offer of selectedOffers) {
      const category = rowFor(offer).category || "non_gaming";
      const payload = {
        offer_id: Number(offer.offer_id),
        name: offer.name,
        category,
        enabled: true,
        display_order: nextOrder(category),
      };
      // Sequential await so display_order stays deterministic.
      // eslint-disable-next-line no-await-in-loop
      results.push(await postOffer(payload));
    }

    const ok = results.filter(Boolean).length;
    const failed = results.length - ok;
    setAdding(false);
    if (ok > 0) {
      notification.success({
        message: "Offers added",
        description: `${ok} offer(s) added to the curated list${
          failed ? `, ${failed} failed` : ""
        }.`,
      });
      onAdded();
      onClose();
    } else {
      notification.error({
        message: "Nothing added",
        description: "All selected offers failed (they may already exist).",
      });
    }
  };

  const goalsPanel = (offer) => {
    const goals = offer.goals || [];
    return (
      <div style={{ paddingLeft: 8 }}>
        {offer.description ? (
          <Paragraph type="secondary" style={{ whiteSpace: "pre-line", marginBottom: 8 }}>
            {offer.description}
          </Paragraph>
        ) : null}
        {goals.length === 0 ? (
          <Text type="secondary">No goal details provided.</Text>
        ) : (
          goals.map((g, i) => (
            <div
              key={i}
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                padding: "8px 12px",
                marginBottom: 8,
              }}
            >
              <Space>
                <Tag color="gold">Step {i + 1}</Tag>
                <Text strong>{g.title || `Step ${i + 1}`}</Text>
                {g.coins != null ? (
                  <Tag color="green">+{formatCoins(g.coins)} coins</Tag>
                ) : null}
              </Space>
              {g.instructions ? (
                <Paragraph
                  style={{ whiteSpace: "pre-line", margin: "6px 0 0", fontSize: 12 }}
                >
                  {g.instructions}
                </Paragraph>
              ) : null}
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <Modal
      title="Browse Live PubScale Offers"
      visible={visible}
      onCancel={onClose}
      width={920}
      maskClosable={false}
      footer={[
        <Button key="refresh" onClick={() => load(true)} disabled={loading || adding}>
          Refresh from PubScale
        </Button>,
        <Button key="cancel" onClick={onClose} disabled={adding}>
          Close
        </Button>,
        <Button
          key="add"
          type="primary"
          loading={adding}
          disabled={selectedOffers.length === 0}
          onClick={handleAdd}
        >
          Add Selected ({selectedOffers.length})
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Input.Search
          placeholder="Search by name or offer id"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        {errorMsg ? <Alert type="error" showIcon message={errorMsg} /> : null}
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin tip="Loading live offers from PubScale…" />
          </div>
        ) : offers.length === 0 && !errorMsg ? (
          <Empty description="No live offers returned" />
        ) : (
          <Table
            dataSource={filtered}
            rowKey="offer_id"
            size="small"
            pagination={{ pageSize: 8, showSizeChanger: false }}
            expandable={{ expandedRowRender: goalsPanel }}
            columns={[
              {
                title: "",
                dataIndex: "select",
                width: 40,
                render: (_, offer) => {
                  const already = existing.has(String(offer.offer_id));
                  return already ? (
                    <Tag color="default">Added</Tag>
                  ) : (
                    <Checkbox
                      checked={rowFor(offer).checked}
                      onChange={(e) =>
                        setRow(offer.offer_id, {
                          checked: e.target.checked,
                          category: rowFor(offer).category,
                        })
                      }
                    />
                  );
                },
              },
              {
                title: "Icon",
                dataIndex: "icon_url",
                width: 56,
                render: (url) =>
                  url ? (
                    <Image src={url} width={36} height={36} style={{ borderRadius: 8 }} />
                  ) : null,
              },
              {
                title: "Offer",
                dataIndex: "name",
                render: (name, offer) => (
                  <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    <Space size={4} wrap>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        #{offer.offer_id}
                      </Text>
                      {(offer.ctg || []).map((c) => (
                        <Tag key={c} style={{ fontSize: 10, marginInlineEnd: 2 }}>
                          {c}
                        </Tag>
                      ))}
                    </Space>
                  </Space>
                ),
              },
              {
                title: "Coins",
                dataIndex: "coins",
                width: 110,
                render: (coins) => <Tag color="gold">+{formatCoins(coins)}</Tag>,
              },
              {
                title: "Category",
                dataIndex: "category",
                width: 150,
                render: (_, offer) => (
                  <Select
                    size="small"
                    value={rowFor(offer).category}
                    style={{ width: 130 }}
                    disabled={existing.has(String(offer.offer_id))}
                    onChange={(value) => setRow(offer.offer_id, { category: value })}
                  >
                    {CATEGORIES.map((c) => (
                      <Option key={c.value} value={c.value}>
                        {c.label}
                      </Option>
                    ))}
                  </Select>
                ),
              },
            ]}
          />
        )}
      </Space>
    </Modal>
  );
};

export default BrowseLiveOffers;
